import { Action, State, StateContext } from '@ngxs/store';
import { AuditStateModel } from '@store/audit/audit-state.model';
import {
    EmitFilterEvent,
    FetchAuditData,
    FetchSectionsWithActions,
    FetchUsersAudit, SearchAuditLogs, SetAuditDates, SetAuditPage, SetSelectedActivity,
    SetSelectedSection, SetSelectsUserIds, SortAuditLogs
} from '@store/audit/audit.actions';
import { Injectable } from '@angular/core';
import { AuditService } from '@services/audit/audit.service';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { IResponse } from '@interfaces/response';
import { IAuditLog, IAuditUser } from '@interfaces/audit';

@State<AuditStateModel>({
    name: 'AuditState',
    defaults: {
        pageNumber: 1,
        range: null,
        start: '',
        end: '',
        search: '',
        sections: [],
        logs: [],
        totalLogs: 0,
        actions: null,
        order: 'DESC',
        itemsPerPage: 10,
        loader: false,
        selectedAction: null,
        selectedSection: null,
        users: [],
        selectedUsersIds: [],
        isFilterSubmitted: false,
        sort: ''
    }
})
@Injectable()
export class AuditState {
    constructor(private auditService: AuditService) {
    }

    @Action(FetchAuditData, {cancelUncompleted: true})
    fetchAuditData(ctx: StateContext<AuditStateModel>): Observable<IResponse<IAuditLog>> {
        const {start, end, selectedSection, search, selectedAction, order} = ctx.getState();
        const limit = ctx.getState().itemsPerPage;
        const skip = limit * (ctx.getState().pageNumber - 1);
        const action = selectedAction || '';
        const section = selectedSection || '';
        const sort = ctx.getState().sort;
        const users = ctx.getState().selectedUsersIds;

        ctx.patchState({loader: true});
        return this.auditService.getAuditData({limit, sort, skip, search, order, start, end, action, section, users})
            .pipe(
                tap(content => {
                    const state = ctx.getState();
                    ctx.setState({...state, logs: content.rows, totalLogs: content.count, loader: false});
                })
            );
    }

    @Action(FetchSectionsWithActions)
    fetchSectionsWithActions(ctx: StateContext<AuditStateModel>): Observable<{ [key: string]: string[] }> {
        return this.auditService.getSectionsWithAction().pipe(tap(data => {
            const state = ctx.getState();
            const sections = Object.keys(data);
            ctx.setState({...state, sections, actions: data});
        }));
    }

    @Action(FetchUsersAudit)
    fetchUsersAudit(ctx: StateContext<AuditStateModel>): Observable<IAuditUser[]> {
        return this.auditService.getUsers().pipe(tap(users => {
            const state = ctx.getState();
            ctx.setState({...state, users});
        }));
    }


    @Action(SetSelectedSection)
    setSelectedSection(ctx: StateContext<AuditStateModel>, {section}: SetSelectedSection): void {
        ctx.patchState({selectedSection: section});
    }

    @Action(SetAuditDates)
    setAuditDates(ctx: StateContext<AuditStateModel>, {payload}: SetAuditDates): void {
        ctx.patchState({range: payload.range, start: payload.start, end: payload.end});
    }

    @Action(SetSelectsUserIds)
    setSelectsUserIds(ctx: StateContext<AuditStateModel>, {userIds}: SetSelectsUserIds): void {
        ctx.patchState({selectedUsersIds: userIds});
    }

    @Action(SetSelectedActivity)
    setSelectedActivity(ctx: StateContext<AuditStateModel>, {activity}: SetSelectedActivity): void {
        ctx.patchState({selectedAction: activity});
    }

    @Action(EmitFilterEvent)
    emitFilterEvent(ctx: StateContext<AuditStateModel>, {submitted}: EmitFilterEvent): Observable<any> {
        ctx.patchState({pageNumber: 1, isFilterSubmitted: submitted});
        return ctx.dispatch(new FetchAuditData());
    }

    @Action(SearchAuditLogs)
    searchAuditLogs(ctx: StateContext<AuditStateModel>, {search}: SearchAuditLogs): Observable<any> {
        ctx.patchState({search});
        return ctx.dispatch(new FetchAuditData());
    }

    @Action(SortAuditLogs)
    sortAuditLogs(ctx: StateContext<AuditStateModel>, {param, order}: SortAuditLogs): Observable<any> {
        ctx.patchState({sort: param, order});
        return ctx.dispatch(new FetchAuditData());
    }

    @Action(SetAuditPage)
    setAuditPage(ctx: StateContext<AuditStateModel>, {page}: SetAuditPage): Observable<any> {
        ctx.patchState({pageNumber: page});
        return ctx.dispatch(new FetchAuditData());
    }
}
